import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { FlaskConical, Plus, X } from 'lucide-react';
import { LabTest } from '../../types/api.types';
import { Modal } from '../common/Modal';
import { LabTestForm } from '../labTests/LabTestForm';
import { useCreateLabTest } from '../../hooks/useLabTests';
import { CreateLabTestDto } from '../../api/labTests.api';
import { SearchableSelect } from '../common/SearchableSelect';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';

interface LabTestSelectorProps {
  labTests: LabTest[];
  selectedLabTestIds: number[];
  onToggleLabTest: (id: number) => void;
  onLabTestAdded?: (labTest: LabTest) => void;
}

const LabTestSelectorComponent: React.FC<LabTestSelectorProps> = ({
  labTests,
  selectedLabTestIds,
  onToggleLabTest,
  onLabTestAdded,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const [selectedLabTestId, setSelectedLabTestId] = useState<number | ''>('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const createMutation = useCreateLabTest();

  const availableOptions = labTests
    .filter((test) => !selectedLabTestIds.includes(test.id))
    .map((test) => ({ value: test.id, label: test.name }));

  const handleSelectLabTest = (value: string | number) => {
    const id = typeof value === 'string' ? parseInt(value, 10) : value;
    if (!selectedLabTestIds.includes(id)) {
      onToggleLabTest(id);
    }
    setSelectedLabTestId(''); // Reset dropdown to placeholder after selection
  };

  const handleAddLabTest = async (data: CreateLabTestDto) => {
    try {
      const newLabTest = await createMutation.mutateAsync(data);
      toast.success(t('labTests.labTestCreated'));
      setIsAddModalOpen(false);
      if (onLabTestAdded) {
        onLabTestAdded(newLabTest);
        onToggleLabTest(newLabTest.id);
      }
    } catch {
      toast.error(t('labTests.labTestCreatedError'));
    }
  };

  return (
    <div className="mb-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div
        className="rounded-3 p-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 50%, #93c5fd 100%)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06)',
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
          <div className="d-flex align-items-center gap-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
              }}
            >
              <FlaskConical className="text-white" size={24} strokeWidth={2.5} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h3 className="mb-0 fw-bold" style={{ color: '#1e40af', fontSize: '1.25rem' }}>
                  {t('prescriptions.labTests')}
                </h3>
                {labTests.length > 0 && (
                  <span
                    className="badge"
                    style={{
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#1e40af',
                      fontSize: '0.7rem',
                      padding: '0.25rem 0.5rem',
                    }}
                  >
                    {labTests.length} {t('prescriptions.available', 'متاح')}
                  </span>
                )}
              </div>
              <p className="mb-0 small" style={{ color: '#1e3a8a', opacity: 0.8 }}>
                {labTests.length > 0
                  ? t('prescriptions.selectLabFromDropdown', 'اختر من القائمة')
                  : t('labTests.noLabTestsAvailable', 'لا توجد تحاليل متاحة. يرجى إضافة تحاليل جديدة.')}
              </p>
            </div>
          </div>
        </div>

        <div className="d-flex gap-2" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
          <div className="flex-grow-1">
            <SearchableSelect
              placeholder={
                labTests.length > 0
                  ? t('prescriptions.selectLabTest', 'اختر تحليل...')
                  : t('common.noTestsAvailable', 'لا توجد تحاليل متاحة')
              }
              value={selectedLabTestId}
              onChange={(value) => {
                const id = value != null && value !== '' ? (typeof value === 'string' ? parseInt(value, 10) : value) : '';
                if (id !== '') handleSelectLabTest(id);
              }}
              options={availableOptions}
              searchable
              disabled={labTests.length === 0}
              clearable={true}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            className="d-flex align-items-center gap-2 fw-semibold"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
              minWidth: '120px',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <Plus className="h-4 w-4" />
            {t('labTests.addNewLabTest', 'إضافة جديد')}
          </Button>
        </div>

        {labTests.length === 0 && (
          <div className="p-3 bg-warning bg-opacity-10 border border-warning rounded mt-3">
            <p className="text-warning mb-2 fw-semibold">{t('labTests.noLabTestsAvailable', 'لا توجد تحاليل متاحة')}</p>
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={() => setIsAddModalOpen(true)}
            >
              {t('labTests.addItNow')}
            </button>
          </div>
        )}
      </div>

      {selectedLabTestIds.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 mb-2">{t('prescriptions.selectedLabTests')}</p>
          {selectedLabTestIds.map((id) => {
            const labTest = labTests.find((lab) => lab.id === id);
            return (
              <div
                key={id}
                className="d-flex align-items-center justify-content-between bg-gray-50 p-3 rounded-lg"
              >
                <span className="fw-medium">{labTest?.name ?? id}</span>
                <button
                  onClick={() => onToggleLabTest(id)}
                  className="btn btn-link text-danger p-0"
                  type="button"
                  aria-label={t('common.remove', 'إزالة')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title={t('labTests.addNewLabTest')}
      >
        <LabTestForm
          onSubmit={handleAddLabTest}
          isLoading={createMutation.isPending}
          onCancel={() => setIsAddModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export const LabTestSelector = React.memo(LabTestSelectorComponent);
